// Filter statistics: which posts each filter matched, counted on this device from
// the filter results the server sends with posts ("filtered"). Kept per account in
// localStorage for 30 days; shown under "Content and filters".

import { me } from "flavours/glitch/initial_state";

const KEEP_MS = 30 * 24 * 3600 * 1000;
const MAX_PER_FILTER = 1000;
const SAVE_DELAY = 1000;

const storageKey = () => `sloth-filter-stats:${me}`;

let cache = null;
let saveTimer = null;

const load = () => {
  if (cache) {
    return cache;
  }

  try {
    cache = JSON.parse(window.localStorage.getItem(storageKey()) || "{}") || {};
  } catch {
    cache = {};
  }

  return cache;
};

const prune = (stats, now) => {
  Object.values(stats).forEach(entry => {
    const hits = Object.entries(entry.hits || {})
      .filter(([, at]) => now - at < KEEP_MS)
      .sort((a, b) => b[1] - a[1])
      .slice(0, MAX_PER_FILTER);
    entry.hits = Object.fromEntries(hits);
  });
};

const scheduleSave = () => {
  clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    const stats = load();
    prune(stats, Date.now());
    try {
      window.localStorage.setItem(storageKey(), JSON.stringify(stats));
    } catch {
      // storage full or unavailable: statistics stay in memory for this visit
    }
  }, SAVE_DELAY);
};

// Called by the importer for every post that arrives with filter results.
export const recordFilterHits = (status) => {
  if (!me || !status?.filtered?.length) {
    return;
  }

  const stats = load();
  const now = Date.now();
  let changed = false;

  status.filtered.forEach(({ filter }) => {
    if (!filter?.id) {
      return;
    }

    const entry = stats[filter.id] || (stats[filter.id] = { title: filter.title, hits: {} });
    entry.title = filter.title || entry.title;

    if (!entry.hits[status.id]) {
      entry.hits[status.id] = now;
      changed = true;
    }
  });

  if (changed) {
    scheduleSave();
  }
};

// { [filterId]: { week, month, last } }
export const readFilterStats = () => {
  const stats = load();
  const now = Date.now();
  const out = {};

  Object.entries(stats).forEach(([id, entry]) => {
    const times = Object.values(entry.hits || {});
    out[id] = {
      title: entry.title,
      week: times.filter(at => now - at < 7 * 24 * 3600 * 1000).length,
      month: times.filter(at => now - at < KEEP_MS).length,
      last: times.length ? Math.max(...times) : null,
    };
  });

  return out;
};

export const resetFilterStats = () => {
  cache = {};
  clearTimeout(saveTimer);
  try {
    window.localStorage.removeItem(storageKey());
  } catch {
    // nothing to remove
  }
};

// When counting started on this device, so "0 hits" can be read correctly.
const SINCE_KEY = () => `sloth-filter-stats-since:${me}`;

export const statsSince = () => {
  try {
    let since = window.localStorage.getItem(SINCE_KEY());
    if (!since) {
      since = String(Date.now());
      window.localStorage.setItem(SINCE_KEY(), since);
    }
    return Number(since);
  } catch {
    return null;
  }
};

export const restartStatsSince = () => {
  try {
    window.localStorage.setItem(SINCE_KEY(), String(Date.now()));
  } catch {
    // ignore
  }
};
